import { useEffect, useMemo } from 'react';

import type {
  EditingMode,
  FieldsInJSON,
  PermissionRole,
  SelBoundaryNode,
  SelectionAffinity,
} from '@aline/common';
import { debounce } from 'debounce';
import { equals } from 'ramda';
import type { Editor, NodeEntry, Point, Range } from 'slate';
import type { ExistingFieldRecipient } from 'src/components/workflow/workflow-collaborators/FieldRecipientAutocomplete';

import type { EditorDerivedState, EditorDocumentUiState } from './editor-derived-state';
import {
  deriveExpensiveEditorDerivedState,
  deriveSelectionDerivedState,
  initialEditorDerivedState,
  setSuggestedEdits,
} from './editor-derived-state';
import { createOwnedTabScopedRegistry } from './tab-scoped-registry';

export type ActiveCommentPanel = 'ViewComments' | 'AddComment' | 'ViewThread';

export interface PointerClick {
  point: Point;
  timestamp: number;
  gridColumn?: number | null;
  skipScrollSelectionIntoView?: boolean;
}

export interface EditorClickState {
  consecutiveClickCount: number;
  lastMouseDownPoint: Point | null;
  lastMouseDownTimestamp: number | null;
  mouseDownClick: PointerClick | null;
}

/**
 * Asks a right panel to scroll a card into view. A fresh `requestId` repeats the request even when
 * the target and the editor selection are unchanged, e.g. clicking the same margin marker twice.
 */
export interface PanelScrollRequest<TTarget> {
  requestId: number;
  target: TTarget;
}

export interface EditorCommentsState {
  activePanel: ActiveCommentPanel;
  commentThreadId: UUID | null;
  scrollRequest: PanelScrollRequest<UUID> | null;
  selectedCommentIds: UUID[];
}

export interface EditorTrackedChangesState {
  scrollRequest: PanelScrollRequest<Range> | null;
}

export interface EditorDropPoint extends Point {
  affinity: SelectionAffinity;
}

export interface EditorDropPdfPageCoords {
  eventPageNum: number;
  pageCoords: {
    x: number;
    y: number;
  };
  windowCoords: {
    x: number;
    y: number;
  };
}

export type EditorMousePageCoords = EditorDropPdfPageCoords | EditorDropPoint;

export interface EditorDragDropState {
  mousePageCoords: EditorMousePageCoords | null;
}

export type ViewerAnnotationsChangedCount = number | 'unset';
export type ViewerSyncingDocState = boolean | 'uninitialized';
export type EditorUserState = NonNullable<Editor['userState']>;

export interface EditorViewerState {
  annotationsChangedCount: ViewerAnnotationsChangedCount;
  isLoading: boolean;
  pageWidthPx: number;
  syncingDoc: ViewerSyncingDocState;
  zoomLevel: number;
}

export interface EditorTabDocumentUiState extends EditorDocumentUiState {
  disconnectedAt: number | null;
  editingMode: EditingMode | null;
  fieldRecipient: ExistingFieldRecipient | null;
  headerFooterDialogOpen: boolean;
  indentationDialogOpen: boolean;
  loadingDocument: boolean;
  pageSetupDialogOpen: boolean;
  permissionRole: PermissionRole | null;
  renameDoc: boolean;
  trashDialogOpen: boolean;
  userState: EditorUserState | null;
}

export interface EditorReactWindowState {
  scrollOffset: number;
  windowHeight: number;
}

export interface EditorTabState {
  click: EditorClickState;
  comments: EditorCommentsState;
  derived: EditorDerivedState;
  documentUi: EditorTabDocumentUiState;
  dragDrop: EditorDragDropState;
  reactWindow: EditorReactWindowState;
  trackedChanges: EditorTrackedChangesState;
  viewer: EditorViewerState;
}

export interface EditorTabStateActions {
  click: {
    setClickCount: (count: number) => void;
    setLastMouseDownPoint: (point: Point | null) => void;
    setLastMouseDownTimestamp: (timestamp: number | null) => void;
    setMouseDownClick: (click: PointerClick | null) => void;
  };
  comments: {
    clearScrollRequest: (requestId: number) => void;
    requestScrollToComment: (commentId: UUID) => void;
    setActivePanel: (activePanel: ActiveCommentPanel) => void;
    setCommentThreadId: (commentThreadId: UUID | null) => void;
    setSelectedCommentIds: (selectedCommentIds: UUID[]) => void;
  };
  derived: {
    deriveExpensiveState: (editor: Editor) => void;
    deriveSelectionState: (editor: Editor) => void;
    setSuggestedEdits: (suggestedEdits: EditorDerivedState['suggestedEdits']) => void;
  };
  documentUi: {
    setDisconnected: (connected: boolean) => void;
    setEditingMode: (editingMode: EditingMode | null) => void;
    setFieldRecipient: (fieldRecipient: ExistingFieldRecipient | null) => void;
    setFooterOpen: (footerOpen: boolean) => void;
    setHeaderFooterDialogOpen: (headerFooterDialogOpen: boolean) => void;
    setHeaderOpen: (headerOpen: boolean) => void;
    setIndentationDialogOpen: (indentationDialogOpen: boolean) => void;
    setLoadingDocument: (loadingDocument: boolean) => void;
    setPageSetupDialogOpen: (pageSetupDialogOpen: boolean) => void;
    setPermissionRole: (permissionRole: PermissionRole | null) => void;
    setRenameDoc: (renameDoc: boolean) => void;
    setTrashDialogOpen: (trashDialogOpen: boolean) => void;
    setUserState: (userState: EditorUserState | null) => void;
  };
  dragDrop: {
    setMousePageCoords: (mousePageCoords: EditorMousePageCoords | null) => void;
  };
  reactWindow: {
    setScrollOffset: (scrollOffset: number) => void;
    setWindowHeight: (windowHeight: number) => void;
  };
  trackedChanges: {
    clearScrollRequest: (requestId: number) => void;
    requestScrollToChange: (range: Range) => void;
  };
  viewer: {
    incrementViewerAnnotationsChangedCount: () => void;
    markViewerDocumentCleared: () => void;
    markViewerDocumentLoaded: () => void;
    markViewerLoadFinished: () => void;
    markViewerLoadStarted: () => void;
    setSyncingDoc: (syncingDoc: ViewerSyncingDocState) => void;
    setViewerPageWidthPx: (pageWidthPx: number) => void;
    setViewerZoomLevel: (zoomLevel: number) => void;
  };
}

interface DebouncedExpensiveUpdate {
  (editor: Editor): void;
  clear: () => void;
}

const initialEditorTabState: EditorTabState = {
  click: {
    consecutiveClickCount: 1,
    lastMouseDownPoint: null,
    lastMouseDownTimestamp: null,
    mouseDownClick: null,
  },
  comments: {
    activePanel: 'ViewComments',
    commentThreadId: null,
    scrollRequest: null,
    selectedCommentIds: [],
  },
  derived: initialEditorDerivedState,
  documentUi: {
    disconnectedAt: null,
    editingMode: null,
    fieldRecipient: null,
    footerOpen: false,
    headerFooterDialogOpen: false,
    headerOpen: false,
    indentationDialogOpen: false,
    loadingDocument: false,
    pageSetupDialogOpen: false,
    permissionRole: null,
    renameDoc: false,
    trashDialogOpen: false,
    userState: null,
  },
  dragDrop: {
    mousePageCoords: null,
  },
  reactWindow: {
    scrollOffset: 0,
    windowHeight: 0,
  },
  trackedChanges: {
    scrollRequest: null,
  },
  viewer: {
    annotationsChangedCount: 'unset',
    isLoading: false,
    pageWidthPx: 0,
    syncingDoc: 'uninitialized',
    zoomLevel: 1,
  },
};

const debouncedExpensiveUpdatesByTabId = new Map<UUID, DebouncedExpensiveUpdate>();

/** The document each tab's state currently describes. */
const openDocumentIdsByTabId = new Map<UUID, UUID>();

const editorTabStateRegistry = createOwnedTabScopedRegistry({
  initialState: initialEditorTabState,
  onPurge: releaseEditorTabResources,
  useTabIdErrorMessage: 'useEditorTabStateActions must be used within an editor tab state scope.',
});

export const EditorTabStateProvider = editorTabStateRegistry.Provider;
export const EditorTabStateScope = editorTabStateRegistry.Scope;

export function useEditorTabStateSelector<TValue>(
  selector: (state: EditorTabState) => TValue,
  isEqual?: (left: TValue, right: TValue) => boolean
): TValue {
  return editorTabStateRegistry.useSelector(selector, isEqual);
}

export function useEditorTabStateSelectorForTabId<TValue>(
  tabId: UUID | null,
  selector: (state: EditorTabState) => TValue,
  isEqual?: (left: TValue, right: TValue) => boolean
): TValue {
  return editorTabStateRegistry.useSelectorForTabId(tabId, selector, isEqual);
}

export function useGetSelBoundaryEntry(): NodeEntry<SelBoundaryNode> | null {
  return useEditorTabStateSelector(state => state.derived.selBoundaryEntry);
}

export function useGetCommentIds(): UUID[] {
  return useEditorTabStateSelector(state => state.derived.commentNodeIds, equals);
}

export function useGetFieldsInJSON(): {
  cannedFieldIdsInChildren: Set<UUID>;
  fieldIdsInChildren: Set<UUID>;
  fieldsInJSON: FieldsInJSON;
} {
  const fieldsInJSON = useEditorTabStateSelector(state => state.derived.fieldsInJSON, equals);

  return useMemo(() => {
    const cannedFieldIdsInChildren = new Set(Object.keys(fieldsInJSON.cannedInChildren));
    const fieldIdsInChildren = new Set(Object.keys(fieldsInJSON.inChildren));

    return {
      cannedFieldIdsInChildren,
      fieldIdsInChildren,
      fieldsInJSON,
    };
  }, [fieldsInJSON]);
}

/**
 * Ties a tab's state to the document the tab currently holds, clearing what belonged to the
 * document it held before.
 *
 * The state outlives every editor that writes to it — it survives a hidden tab pane, and it
 * survives the tab visiting a page that has no editor at all — so the document it describes is
 * recorded next to the state rather than in any component. Comparing against that record is what
 * makes this safe to run again: setting up on a tab switch, or mounting a fresh editor for the
 * document already recorded, changes nothing, while any other document clears the previous one's
 * state first.
 */
export function useBindEditorTabStateToDocument(tabId: UUID, documentId: UUID): void {
  useEffect(() => {
    bindEditorTabStateToDocument(tabId, documentId);
  }, [documentId, tabId]);
}

export function useEditorTabStateActions(): EditorTabStateActions {
  const tabId = editorTabStateRegistry.useTabId();
  return useEditorTabStateActionsForTabId(tabId);
}

export function useEditorTabStateActionsForTabId(tabId: UUID): EditorTabStateActions {
  return useMemo(() => createEditorTabStateActions(tabId), [tabId]);
}

export function getEditorTabStateActionsForTabId(tabId: UUID): EditorTabStateActions {
  return createEditorTabStateActions(tabId);
}

export function getEditorTabStateForTabId(tabId: UUID | null): EditorTabState | null {
  return editorTabStateRegistry.getForTabId(tabId);
}

export function updateEditorTabStateForTabId(
  tabId: UUID,
  updater: (state: EditorTabState) => EditorTabState
): void {
  editorTabStateRegistry.updateForTabId(tabId, updater);
}

export function updateSelectionEditorDerivedStateForTabId(tabId: UUID, editor: Editor) {
  const previousState = getEditorTabStateForTabId(tabId) ?? initialEditorTabState;
  const { sideEffects, state: derived } = deriveSelectionDerivedState(
    editor,
    previousState.derived,
    previousState.documentUi
  );

  updateEditorTabStateForTabId(tabId, state => {
    const documentUi = getDocumentUiWithPatch(state.documentUi, sideEffects.documentUi);
    const comments = getCommentsWithSelectedIds(state.comments, sideEffects.selectedCommentIds);
    const willReturnPreviousState =
      state.derived === derived && state.comments === comments && state.documentUi === documentUi;

    if (willReturnPreviousState) return state;

    return { ...state, comments, derived, documentUi };
  });
}

export function debounceUpdateExpensiveEditorDerivedStateForTabId(
  tabId: UUID,
  editor: Editor
): void {
  getDebouncedExpensiveUpdateForTabId(tabId)(editor);
}

export function updateExpensiveEditorDerivedStateForTabId(tabId: UUID, editor: Editor): void {
  updateEditorTabStateForTabId(tabId, state => {
    const derived = deriveExpensiveEditorDerivedState(editor, state.derived);
    if (state.derived === derived) return state;
    return { ...state, derived };
  });
}

/**
 * Clears the parts of a tab's state that name nodes or ids inside one specific document, so the
 * next document opened in that tab cannot inherit them, and records the document now described.
 *
 * `documentUi` and `viewer` are deliberately left alone: each is republished for the document the
 * tab now holds by the code that owns it (editing permissions, the file viewer's load cycle), and
 * several of those effects only re-run when their own inputs change, so blanking their values here
 * can leave them empty. `reactWindow` belongs to `Document`, which publishes the scroller's real
 * offset and height whenever it mounts for the new document.
 */
function bindEditorTabStateToDocument(tabId: UUID, documentId: UUID) {
  if (openDocumentIdsByTabId.get(tabId) === documentId) return;

  openDocumentIdsByTabId.set(tabId, documentId);
  cancelDebouncedExpensiveUpdateForTabId(tabId);
  updateEditorTabStateForTabId(tabId, state => ({
    ...state,
    click: initialEditorTabState.click,
    comments: initialEditorTabState.comments,
    derived: initialEditorTabState.derived,
    dragDrop: initialEditorTabState.dragDrop,
    trackedChanges: initialEditorTabState.trackedChanges,
  }));
}

function createEditorTabStateActions(tabId: UUID): EditorTabStateActions {
  return {
    click: {
      setClickCount: consecutiveClickCount =>
        updateClickState(tabId, state => ({ ...state, consecutiveClickCount })),
      setLastMouseDownPoint: lastMouseDownPoint =>
        updateClickState(tabId, state => ({ ...state, lastMouseDownPoint })),
      setLastMouseDownTimestamp: lastMouseDownTimestamp =>
        updateClickState(tabId, state => ({ ...state, lastMouseDownTimestamp })),
      setMouseDownClick: mouseDownClick =>
        updateClickState(tabId, state => ({ ...state, mouseDownClick })),
    },
    comments: {
      clearScrollRequest: requestId =>
        updateCommentsState(tabId, state => ({
          ...state,
          scrollRequest: getClearedScrollRequest(state.scrollRequest, requestId),
        })),
      requestScrollToComment: commentId =>
        updateCommentsState(tabId, state => ({
          ...state,
          scrollRequest: getNextScrollRequest(state.scrollRequest, commentId),
        })),
      setActivePanel: activePanel =>
        updateCommentsState(tabId, state => ({ ...state, activePanel })),
      setCommentThreadId: commentThreadId =>
        updateCommentsState(tabId, state => ({ ...state, commentThreadId })),
      setSelectedCommentIds: selectedCommentIds =>
        updateCommentsState(tabId, state => getCommentsWithSelectedIds(state, selectedCommentIds)),
    },
    derived: {
      deriveExpensiveState: editor => updateExpensiveEditorDerivedStateForTabId(tabId, editor),
      deriveSelectionState: editor => updateSelectionEditorDerivedStateForTabId(tabId, editor),
      setSuggestedEdits: suggestedEdits =>
        updateEditorTabStateForTabId(tabId, state => {
          const derived = setSuggestedEdits(state.derived, suggestedEdits);
          if (state.derived === derived) return state;
          return { ...state, derived };
        }),
    },
    documentUi: {
      setDisconnected: connected =>
        updateDocumentUiState(tabId, state => ({
          ...state,
          disconnectedAt: connected ? null : Date.now(),
        })),
      setEditingMode: editingMode =>
        updateDocumentUiState(tabId, state => ({ ...state, editingMode })),
      setFieldRecipient: fieldRecipient =>
        updateDocumentUiState(tabId, state => ({ ...state, fieldRecipient })),
      setFooterOpen: footerOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, footerOpen })),
      setHeaderFooterDialogOpen: headerFooterDialogOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, headerFooterDialogOpen })),
      setHeaderOpen: headerOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, headerOpen })),
      setIndentationDialogOpen: indentationDialogOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, indentationDialogOpen })),
      setLoadingDocument: loadingDocument =>
        updateDocumentUiState(tabId, state => ({ ...state, loadingDocument })),
      setPageSetupDialogOpen: pageSetupDialogOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, pageSetupDialogOpen })),
      setPermissionRole: permissionRole =>
        updateDocumentUiState(tabId, state => ({ ...state, permissionRole })),
      setRenameDoc: renameDoc => updateDocumentUiState(tabId, state => ({ ...state, renameDoc })),
      setTrashDialogOpen: trashDialogOpen =>
        updateDocumentUiState(tabId, state => ({ ...state, trashDialogOpen })),
      setUserState: userState => updateDocumentUiState(tabId, state => ({ ...state, userState })),
    },
    dragDrop: {
      setMousePageCoords: mousePageCoords =>
        updateDragDropState(tabId, state => ({ ...state, mousePageCoords })),
    },
    reactWindow: {
      setScrollOffset: scrollOffset =>
        updateReactWindowState(tabId, state => ({ ...state, scrollOffset })),
      setWindowHeight: windowHeight =>
        updateReactWindowState(tabId, state => ({ ...state, windowHeight })),
    },
    trackedChanges: {
      clearScrollRequest: requestId =>
        updateTrackedChangesState(tabId, state => ({
          ...state,
          scrollRequest: getClearedScrollRequest(state.scrollRequest, requestId),
        })),
      requestScrollToChange: range =>
        updateTrackedChangesState(tabId, state => ({
          ...state,
          scrollRequest: getNextScrollRequest(state.scrollRequest, range),
        })),
    },
    viewer: {
      incrementViewerAnnotationsChangedCount: () =>
        updateViewerState(tabId, state => ({
          ...state,
          annotationsChangedCount:
            state.annotationsChangedCount === 'unset' ? 1 : state.annotationsChangedCount + 1,
        })),
      markViewerDocumentCleared: () =>
        updateViewerState(tabId, state => ({
          ...state,
          annotationsChangedCount: 'unset',
          isLoading: false,
          pageWidthPx: 0,
        })),
      markViewerDocumentLoaded: () =>
        updateViewerState(tabId, state => ({
          ...state,
          annotationsChangedCount: 0,
          isLoading: false,
        })),
      markViewerLoadFinished: () =>
        updateViewerState(tabId, state => ({ ...state, isLoading: false })),
      markViewerLoadStarted: () =>
        updateViewerState(tabId, state => ({ ...state, isLoading: true })),
      setSyncingDoc: syncingDoc => updateViewerState(tabId, state => ({ ...state, syncingDoc })),
      setViewerPageWidthPx: pageWidthPx =>
        updateViewerState(tabId, state => ({ ...state, pageWidthPx })),
      setViewerZoomLevel: zoomLevel => updateViewerState(tabId, state => ({ ...state, zoomLevel })),
    },
  };
}

function updateClickState(tabId: UUID, updater: (state: EditorClickState) => EditorClickState) {
  updateEditorTabStateForTabId(tabId, state => {
    const click = updater(state.click);
    if (state.click === click || equals(state.click, click)) return state;
    return { ...state, click };
  });
}

function updateCommentsState(
  tabId: UUID,
  updater: (state: EditorCommentsState) => EditorCommentsState
) {
  updateEditorTabStateForTabId(tabId, state => {
    const comments = updater(state.comments);
    if (state.comments === comments || equals(state.comments, comments)) return state;
    return { ...state, comments };
  });
}

function updateDocumentUiState(
  tabId: UUID,
  updater: (state: EditorTabDocumentUiState) => EditorTabDocumentUiState
) {
  updateEditorTabStateForTabId(tabId, state => {
    const documentUi = updater(state.documentUi);
    if (state.documentUi === documentUi || equals(state.documentUi, documentUi)) return state;
    return { ...state, documentUi };
  });
}

function updateDragDropState(
  tabId: UUID,
  updater: (state: EditorDragDropState) => EditorDragDropState
) {
  updateEditorTabStateForTabId(tabId, state => {
    const dragDrop = updater(state.dragDrop);
    if (state.dragDrop === dragDrop || equals(state.dragDrop, dragDrop)) return state;
    return { ...state, dragDrop };
  });
}

function updateReactWindowState(
  tabId: UUID,
  updater: (state: EditorReactWindowState) => EditorReactWindowState
) {
  updateEditorTabStateForTabId(tabId, state => {
    const reactWindow = updater(state.reactWindow);
    if (state.reactWindow === reactWindow || equals(state.reactWindow, reactWindow)) return state;
    return { ...state, reactWindow };
  });
}

function updateTrackedChangesState(
  tabId: UUID,
  updater: (state: EditorTrackedChangesState) => EditorTrackedChangesState
) {
  updateEditorTabStateForTabId(tabId, state => {
    const trackedChanges = updater(state.trackedChanges);
    if (state.trackedChanges === trackedChanges || equals(state.trackedChanges, trackedChanges))
      return state;
    return { ...state, trackedChanges };
  });
}

function updateViewerState(tabId: UUID, updater: (state: EditorViewerState) => EditorViewerState) {
  updateEditorTabStateForTabId(tabId, state => {
    const viewer = updater(state.viewer);
    if (state.viewer === viewer || equals(state.viewer, viewer)) return state;
    return { ...state, viewer };
  });
}

function getNextScrollRequest<TTarget>(
  previous: PanelScrollRequest<TTarget> | null,
  target: TTarget
): PanelScrollRequest<TTarget> {
  return { requestId: (previous?.requestId ?? 0) + 1, target };
}

// Only the request that was handled is cleared, so a newer one queued meanwhile survives.
function getClearedScrollRequest<TTarget>(
  scrollRequest: PanelScrollRequest<TTarget> | null,
  requestId: number
) {
  return scrollRequest?.requestId === requestId ? null : scrollRequest;
}

function getCommentsWithSelectedIds(
  comments: EditorCommentsState,
  selectedCommentIds: UUID[]
): EditorCommentsState {
  if (equals(comments.selectedCommentIds, selectedCommentIds)) return comments;
  return { ...comments, selectedCommentIds };
}

function getDocumentUiWithPatch(
  documentUi: EditorTabDocumentUiState,
  patch: Partial<EditorDocumentUiState>
): EditorTabDocumentUiState {
  const headerOpen = patch.headerOpen ?? documentUi.headerOpen;
  const footerOpen = patch.footerOpen ?? documentUi.footerOpen;

  if (documentUi.headerOpen === headerOpen && documentUi.footerOpen === footerOpen)
    return documentUi;
  return { ...documentUi, footerOpen, headerOpen };
}

/** Drops everything held for a tab beside its state, for when that state is thrown away. */
function releaseEditorTabResources(tabId: UUID) {
  cancelDebouncedExpensiveUpdateForTabId(tabId);
  openDocumentIdsByTabId.delete(tabId);
}

function cancelDebouncedExpensiveUpdateForTabId(tabId: UUID) {
  const debouncedExpensiveUpdate = debouncedExpensiveUpdatesByTabId.get(tabId);
  debouncedExpensiveUpdate?.clear();
  debouncedExpensiveUpdatesByTabId.delete(tabId);
}

function getDebouncedExpensiveUpdateForTabId(tabId: UUID) {
  const existingUpdate = debouncedExpensiveUpdatesByTabId.get(tabId);
  if (existingUpdate) return existingUpdate;

  const debouncedUpdate = debounce((editor: Editor) => {
    updateExpensiveEditorDerivedStateForTabId(tabId, editor);
  }, 500);
  debouncedExpensiveUpdatesByTabId.set(tabId, debouncedUpdate);

  return debouncedUpdate;
}
